import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import Home from './components/Home';
import Login from './components/Login';
import NovoCliente from './components/NovoCliente';
import ListaClientes from './components/ListaClientes';
import ListaUsuarios from './components/ListaUsuarios';
import ListaZenPDV from './components/ListaZenPDV';
import ListaZenGestao from './components/ListaZenGestao';
import ListaZenTag from './components/ListaZenTag';
import ListaZenMobile from './components/ListaZenMobile';
import ListaZenPass from './components/ListaZenPass';
import ZenPassUsuarios from './components/ZenPassUsuarios';

import EditarCliente from './components/EditarCliente';
import EditarUsuario from './components/EditarUsuario';
import NovoUsuario from './components/NovoUsuario';
import EditarPdv from './components/EditarPdv';
import NovoPDV from './components/NovoPdv';
import UploadCertificado from './components/UploadCertificado';

import Layout from './components/Layout';
import ListaTabelasPreco from './components/ListaTabelasPreco';
import FormularioTabelaPreco from './components/FormularioTabelaPreco';
import NovoClienteZenTagForm from './components/FormularioZenTag';
import NovoClienteZenGestaoForm from './components/FormularioZenGestao';
import NovoClienteZenMobileForm from './components/FormularioZenMobile';
import NovoClienteZenPassForm from './components/FormularioZenPass';

import ListaRevendas from './components/ListaRevendas';
import NovoRevenda from './components/NovoRevenda';
import EditarRevenda from './components/EditarRevenda';
import RevendaLicencas from './components/RevendaLicencas';
import RevendaUsuarios from './components/RevendaUsuarios';
import NovoRevendaUsuario from './components/NovoRevendaUsuario';
import EditarRevendaUsuario from './components/EditarRevendaUsuario';

import ZentagUsuarios from './components/ZentagUsuarios';

import ListaVendedores from './components/ListaVendedores';
import NovoVendedor from './components/NovoVendedor';
import EditarVendedor from './components/EditarVendedor';
import RelatoriosLicencas from './components/RelatoriosLicencas';
import ListaDistribuidores from './components/ListaDistribuidores';
import NovoDistribuidor from './components/NovoDistribuidor';
import EditarDistribuidor from './components/EditarDistribuidor';
import DistribuidorUsuarios from './components/DistribuidorUsuarios';
import NovoDistribuidorUsuario from './components/NovoDistribuidorUsuario';
import EditarDistribuidorUsuario from './components/EditarDistribuidorUsuario';
import RelatorioDistribuidor from './components/RelatorioDistribuidor';
import RelatorioRevenda from './components/RelatorioRevenda';
import AuditoriaApi from './components/AuditoriaApi';
import PrivateRoute from './components/PrivateRoute';
import { Permissoes } from './utils/permissoes';
import { clearAuthSession, isTokenExpired, setupInterceptors } from './services/api';

function AppRoutes() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => sessionStorage.getItem('token'));
  const [userName, setUserName] = useState(() => sessionStorage.getItem('userName') || '');

  const handleSessionLost = () => {
    clearAuthSession();
    setToken(null);
    setUserName('');
  };

  useEffect(() => {
    setupInterceptors(navigate, handleSessionLost);
  }, [navigate]);

  useEffect(() => {
    const stored = sessionStorage.getItem('token');
    if (stored && isTokenExpired(stored)) {
      handleSessionLost();
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    handleSessionLost();
    navigate('/login', { replace: true });
  };

  const isAuthenticated = !!token && !isTokenExpired(token);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          !isAuthenticated ? (
            <Login setToken={setToken} setUserName={setUserName} />
          ) : (
            <Navigate to="/home" />
          )
        }
      />
      <Route path="/" element={isAuthenticated ? <Navigate to="/home" /> : <Navigate to="/login" />} />

      {isAuthenticated && (
        <Route element={<Layout userName={userName} onLogout={handleLogout} />}>
          <Route path="/home" element={<PrivateRoute token={token}><Home token={token} /></PrivateRoute>} />
          <Route path="/clientes" element={<PrivateRoute token={token}><ListaClientes token={token} /></PrivateRoute>} />
          <Route path="/clientes/novo" element={<PrivateRoute token={token}><NovoCliente token={token} /></PrivateRoute>} />
          <Route path="/clientes/editar/:id" element={<PrivateRoute token={token}><EditarCliente token={token} /></PrivateRoute>} />
          <Route path="/usuarios" element={<PrivateRoute token={token}><ListaUsuarios token={token} /></PrivateRoute>} />
          <Route path="/usuarios/novo" element={<PrivateRoute token={token}><NovoUsuario token={token} /></PrivateRoute>} />
          <Route path="/usuarios/editar/:id" element={<PrivateRoute token={token}><EditarUsuario token={token} /></PrivateRoute>} />
          <Route path="/pdvs/novo" element={<PrivateRoute token={token}><NovoPDV token={token} /></PrivateRoute>} />
          <Route path="/pdvs/editar/:id" element={<PrivateRoute token={token}><EditarPdv token={token} /></PrivateRoute>} />
          <Route path="/certificados/upload" element={<PrivateRoute token={token}><UploadCertificado token={token} /></PrivateRoute>} />

          <Route path="/licenciamento/zenpdv" element={<PrivateRoute token={token}><ListaZenPDV token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zentag/list" element={<PrivateRoute token={token}><ListaZenTag token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zentag/:id/usuarios" element={<PrivateRoute token={token}><ZentagUsuarios token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zentag/novo" element={<PrivateRoute token={token}><NovoClienteZenTagForm token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zentag/editar/:id" element={<NovoClienteZenTagForm token={token} />} />
          <Route path="/licenciamento/zenpass/list" element={<PrivateRoute token={token}><ListaZenPass token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zenpass/novo" element={<PrivateRoute token={token}><NovoClienteZenPassForm token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zenpass/editar/:id" element={<PrivateRoute token={token}><NovoClienteZenPassForm token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zenpass/:id/usuarios" element={<PrivateRoute token={token}><ZenPassUsuarios token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zenmobile/list" element={<PrivateRoute token={token}><ListaZenMobile token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zenmobile/novo" element={<PrivateRoute token={token}><NovoClienteZenMobileForm token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zengestao/list" element={<PrivateRoute token={token}><ListaZenGestao token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zengestao/novo" element={<PrivateRoute token={token}><NovoClienteZenGestaoForm token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zenmobile/editar/:id" element={<PrivateRoute token={token}><NovoClienteZenMobileForm token={token} /></PrivateRoute>} />
          <Route path="/licenciamento/zengestao/editar/:id" element={<PrivateRoute token={token}><NovoClienteZenGestaoForm token={token} /></PrivateRoute>} />
          <Route path="/tabelas-preco" element={<PrivateRoute token={token}><ListaTabelasPreco token={token} /></PrivateRoute>} />
          <Route path="/tabelas-preco/novo" element={<PrivateRoute token={token}><FormularioTabelaPreco token={token} /></PrivateRoute>} />
          <Route path="/tabelas-preco/editar/:id" element={<PrivateRoute token={token}><FormularioTabelaPreco token={token} /></PrivateRoute>} />
          <Route path="/revendas" element={<PrivateRoute token={token}><ListaRevendas token={token} /></PrivateRoute>} />
          <Route path="/revendas/novo" element={<PrivateRoute token={token}><NovoRevenda token={token} /></PrivateRoute>} />
          <Route path="/revendas/editar/:id" element={<PrivateRoute token={token}><EditarRevenda token={token} /></PrivateRoute>} />
          <Route path="/revendas/:revendaId/licencas" element={<PrivateRoute token={token}><RevendaLicencas token={token} /></PrivateRoute>} />
          <Route path="/revendas/:revendaId/usuarios" element={<PrivateRoute token={token}><RevendaUsuarios token={token} /></PrivateRoute>} />
          <Route path="/revendas/usuarios" element={<PrivateRoute token={token}><RevendaUsuarios token={token} /></PrivateRoute>} />
          <Route path="/revendas/usuarios/novo" element={<PrivateRoute token={token}><NovoRevendaUsuario token={token} /></PrivateRoute>} />
          <Route path="/revendas/usuarios/editar/:id" element={<PrivateRoute token={token}><EditarRevendaUsuario token={token} /></PrivateRoute>} />
          <Route path="/vendedores" element={<PrivateRoute token={token}><ListaVendedores token={token} /></PrivateRoute>} />
          <Route path="/vendedores/novo" element={<PrivateRoute token={token}><NovoVendedor token={token} /></PrivateRoute>} />
          <Route path="/vendedores/editar/:id" element={<PrivateRoute token={token}><EditarVendedor token={token} /></PrivateRoute>} />
          <Route path="/relatorios/licencas" element={<PrivateRoute token={token} permission={Permissoes.RelatorioAcessar}><RelatoriosLicencas token={token} /></PrivateRoute>} />
          <Route path="/relatorios/distribuidor" element={<PrivateRoute token={token}><RelatorioDistribuidor token={token} /></PrivateRoute>} />
          <Route path="/relatorios/revenda" element={<PrivateRoute token={token}><RelatorioRevenda token={token} /></PrivateRoute>} />
          <Route path="/distribuidores" element={<PrivateRoute token={token}><ListaDistribuidores token={token} /></PrivateRoute>} />
          <Route path="/distribuidores/novo" element={<PrivateRoute token={token}><NovoDistribuidor token={token} /></PrivateRoute>} />
          <Route path="/distribuidores/editar/:id" element={<PrivateRoute token={token}><EditarDistribuidor token={token} /></PrivateRoute>} />
          <Route path="/distribuidores/usuarios" element={<PrivateRoute token={token}><DistribuidorUsuarios token={token} /></PrivateRoute>} />
          <Route path="/distribuidores/usuarios/novo" element={<PrivateRoute token={token}><NovoDistribuidorUsuario token={token} /></PrivateRoute>} />
          <Route path="/distribuidores/usuarios/editar/:id" element={<PrivateRoute token={token}><EditarDistribuidorUsuario token={token} /></PrivateRoute>} />
          <Route path="/auditoria/api-logs" element={<PrivateRoute token={token} permission={Permissoes.AuditoriaAcessar}><AuditoriaApi token={token} /></PrivateRoute>} />
        </Route>
      )}
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
